import { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Upload, Download, FileSpreadsheet, Loader2,
  CheckSquare, Square, AlertTriangle, CheckCircle, Info
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const enconOrange = '#ee7203';

const OpsReviewModal = ({ open, onClose }) => {
  // Step: 'upload' | 'month-picker' | 'review'
  const [step, setStep] = useState('upload');
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [saving, setSaving] = useState(false);

  // Parsed file info
  const [uploadedFile, setUploadedFile] = useState(null);
  const [parseResult, setParseResult] = useState(null);

  // Error log entries from DB
  const [errorEntries, setErrorEntries] = useState([]);
  const [selectedErrors, setSelectedErrors] = useState({});

  // Month selector (YYYY-MM)
  const currentMonthStr = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  };
  const [selectedMonth, setSelectedMonth] = useState(currentMonthStr());
  const [fetchingMonth, setFetchingMonth] = useState(false);

  const fileInputRef = useRef(null);

  const resetAll = () => {
    setStep('upload');
    setUploading(false);
    setDragging(false);
    setSaving(false);
    setUploadedFile(null);
    setParseResult(null);
    setErrorEntries([]);
    setSelectedErrors({});
    setSelectedMonth(currentMonthStr());
    setFetchingMonth(false);
  };

  const handleClose = () => {
    resetAll();
    onClose();
  };

  const getToken = () => localStorage.getItem('token');

  // Upload and parse the Ops Review file
  const handleFileUpload = async (file, monthOverride = null) => {
    if (!file) return;
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (monthOverride) formData.append('month', monthOverride);

      const res = await fetch(`${API}/api/ops-review/parse`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to parse file');

      setUploadedFile(file);
      setParseResult(data);

      // If no External Transport tab AND the user hasn't picked a month yet,
      // prompt for a month first (errors are pulled from the DB, so we always need a month)
      if (!monthOverride && !data.has_ext_transport) {
        setStep('month-picker');
        return;
      }

      // Apply error entries
      if (data.error_entries && data.error_entries.length > 0) {
        setErrorEntries(data.error_entries);
        const pickingSelected = {};
        data.error_entries.forEach((entry, i) => {
          const isPicking = (entry.error_types || []).some(t => t.toLowerCase() === 'picking');
          if (isPicking) pickingSelected[i] = true;
        });
        setSelectedErrors(pickingSelected);
      } else {
        setErrorEntries([]);
        setSelectedErrors({});
      }
      // Keep the month dropdown in sync with whatever the backend actually used
      if (data.month_used) setSelectedMonth(data.month_used);

      setStep('review');

      if (data.has_ext_transport) {
        toast.success(`File parsed: ${data.ext_transport_orders} transport orders found`);
      } else if (monthOverride) {
        toast.success(`Loaded ${data.error_entries?.length || 0} error entries for ${monthOverride}`);
      } else {
        toast.info('No External Transport tab found — you can still populate the Error Log');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  // Re-fetch errors for a different month (used from review screen too)
  const fetchErrorsForMonth = async (month) => {
    if (!uploadedFile) return;
    setFetchingMonth(true);
    try {
      const fd = new FormData();
      fd.append('file', uploadedFile);
      fd.append('month', month);
      const res = await fetch(`${API}/api/ops-review/parse`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to fetch errors for month');
      setParseResult(data);
      setErrorEntries(data.error_entries || []);
      const pickingSelected = {};
      (data.error_entries || []).forEach((entry, i) => {
        const isPicking = (entry.error_types || []).some(t => t.toLowerCase() === 'picking');
        if (isPicking) pickingSelected[i] = true;
      });
      setSelectedErrors(pickingSelected);
      setSelectedMonth(month);
      toast.success(`Loaded ${data.error_entries?.length || 0} errors for ${month}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setFetchingMonth(false);
    }
  };

  // Build a list of selectable months (current + previous 11)
  const monthOptions = (() => {
    const opts = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const label = d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
      opts.push({ value: `${y}-${m}`, label: i === 0 ? `${label} (current)` : label });
    }
    return opts;
  })();

  // Toggle error selection
  const toggleError = (idx) => {
    setSelectedErrors(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const selectAllErrors = () => {
    const allSelected = Object.keys(selectedErrors).length === errorEntries.length
      && Object.values(selectedErrors).every(Boolean);
    if (allSelected) {
      setSelectedErrors({});
    } else {
      const all = {};
      errorEntries.forEach((_, i) => { all[i] = true; });
      setSelectedErrors(all);
    }
  };

  const selectedCount = Object.values(selectedErrors).filter(Boolean).length;

  // Separate picking errors from others
  const pickingErrors = errorEntries
    .map((e, i) => ({ ...e, _idx: i }))
    .filter(e => (e.error_types || []).some(t => t.toLowerCase() === 'picking'));
  const otherErrors = errorEntries
    .map((e, i) => ({ ...e, _idx: i }))
    .filter(e => !(e.error_types || []).some(t => t.toLowerCase() === 'picking'));

  // Save and download
  const handleSaveDownload = async () => {
    if (!uploadedFile) return;
    setSaving(true);
    try {
      const selectedIndices = Object.entries(selectedErrors)
        .filter(([, v]) => v)
        .map(([k]) => parseInt(k));

      const selectedEntries = selectedIndices.map(i => errorEntries[i]);

      const formData = new FormData();
      formData.append('file', uploadedFile);
      formData.append('error_entries_json', JSON.stringify(selectedEntries));

      const res = await fetch(`${API}/api/ops-review/generate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to generate file');
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = res.headers.get('Content-Disposition');
      let filename = uploadedFile.name.replace('.xlsx', '_updated.xlsx');
      if (disposition) {
        const match = disposition.match(/filename="?(.+?)"?$/);
        if (match) filename = match[1];
      }
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Updated file downloaded');
      handleClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Format date for display
  const fmtDate = (d) => {
    if (!d) return '—';
    const parts = d.split('-');
    if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0].slice(2)}`;
    return d;
  };

  const renderErrorRow = (entry) => {
    const idx = entry._idx;
    const isSelected = selectedErrors[idx];
    return (
      <tr
        key={idx}
        onClick={() => toggleError(idx)}
        className={`border-b cursor-pointer transition-colors ${
          isSelected ? 'bg-orange-50 hover:bg-orange-100' : 'hover:bg-gray-50'
        }`}
        data-testid={`error-row-${idx}`}
      >
        <td className="px-3 py-2 text-center">
          {isSelected
            ? <CheckSquare className="w-4 h-4 inline" style={{ color: enconOrange }} />
            : <Square className="w-4 h-4 inline text-gray-300" />}
        </td>
        <td className="px-3 py-2 text-sm whitespace-nowrap">{fmtDate(entry.date)}</td>
        <td className="px-3 py-2 text-sm">{(entry.names || []).join(', ')}</td>
        <td className="px-3 py-2 text-sm">
          <div className="flex flex-wrap gap-1">
            {(entry.error_types || []).map((t, i) => (
              <span key={i} className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                t.toLowerCase() === 'picking' ? 'bg-red-100 text-red-700' :
                t.toLowerCase() === 'checking' ? 'bg-yellow-100 text-yellow-700' :
                t.toLowerCase() === 'loading' ? 'bg-blue-100 text-blue-700' :
                'bg-gray-100 text-gray-700'
              }`}>{t}</span>
            ))}
          </div>
        </td>
        <td className="px-3 py-2 text-sm">{entry.order_number || '—'}</td>
        <td className="px-3 py-2 text-sm max-w-[250px] truncate" title={entry.what_happened}>
          {entry.what_happened || '—'}
        </td>
        <td className="px-3 py-2 text-sm">{entry.customer_name || '—'}</td>
      </tr>
    );
  };

  const renderErrorSection = (title, entries, color) => {
    if (entries.length === 0) return null;
    return (
      <div className="mb-6">
        <h3 className="text-sm font-bold uppercase tracking-wider mb-2" style={{ color }}>
          {title} ({entries.length})
        </h3>
        <div className="overflow-auto border rounded-xl">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-700 text-white sticky top-0 z-10">
                <th className="px-3 py-2 w-10">
                  <span className="sr-only">Select</span>
                </th>
                <th className="px-3 py-2 text-left font-medium">Date</th>
                <th className="px-3 py-2 text-left font-medium">Person</th>
                <th className="px-3 py-2 text-left font-medium">Type</th>
                <th className="px-3 py-2 text-left font-medium">Ticket</th>
                <th className="px-3 py-2 text-left font-medium">What Happened</th>
                <th className="px-3 py-2 text-left font-medium">Customer</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(renderErrorRow)}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className={step === 'upload' ? 'bg-white rounded-2xl max-w-lg' : 'max-w-[90vw] w-[90vw] max-h-[90vh] p-0 overflow-hidden'}
        data-testid="ops-review-modal"
      >
        {step === 'upload' ? (
          <>
            <DialogHeader className="px-6 pt-6">
              <DialogTitle className="flex items-center gap-2 text-xl font-bold">
                <FileSpreadsheet className="w-6 h-6 text-green-600" />
                BI EXT Transport
              </DialogTitle>
            </DialogHeader>
            <div className="px-6 pb-6 pt-2">
              <p className="text-sm text-gray-600 mb-4">
                Upload an Ops Review Excel file. This will populate External Transport costs and allow you to add Error Log entries from the system.
              </p>
              <div
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragging(false);
                  handleFileUpload(e.dataTransfer.files[0]);
                }}
                onClick={() => !uploading && fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
                  dragging ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-gray-400'
                }`}
                data-testid="ops-review-upload-zone"
              >
                {uploading ? (
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-10 h-10 text-green-600 animate-spin" />
                    <p className="text-gray-600 font-medium">Parsing file...</p>
                  </div>
                ) : (
                  <>
                    <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 font-medium mb-2">
                      Drag and drop your Excel file here
                    </p>
                    <p className="text-sm text-gray-500 mb-4">or click to browse</p>
                    <p className="text-xs text-gray-400">Supported: .xlsx, .xls</p>
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files[0]) handleFileUpload(e.target.files[0]);
                  e.target.value = '';
                }}
              />
            </div>
          </>
        ) : step === 'month-picker' ? (
          <>
            <DialogHeader className="px-6 pt-6">
              <DialogTitle className="flex items-center gap-2 text-xl font-bold">
                <FileSpreadsheet className="w-6 h-6 text-amber-600" />
                Select Error Log Month
              </DialogTitle>
            </DialogHeader>
            <div className="px-6 pb-6 pt-2">
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl mb-4">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-900">No External Transport tab detected</p>
                  <p className="text-xs text-amber-800 mt-1">
                    The file <strong>{uploadedFile?.name}</strong> doesn't have an External Transport tab. Which month's errors would you like to pull through to the Error Log?
                    {parseResult?.sheets?.length > 0 && (
                      <span className="block mt-1 text-amber-700">Sheets found: {parseResult.sheets.join(', ')}</span>
                    )}
                  </p>
                </div>
              </div>
              <label htmlFor="ops-month-select" className="block text-sm font-semibold text-gray-700 mb-2">
                Error log month
              </label>
              <select
                id="ops-month-select"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                data-testid="ops-month-select"
              >
                {monthOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <div className="flex justify-end gap-2 mt-6">
                <Button
                  variant="outline"
                  onClick={() => { resetAll(); }}
                  className="rounded-xl text-sm"
                  data-testid="ops-month-cancel"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleFileUpload(uploadedFile, selectedMonth)}
                  disabled={uploading}
                  className="text-white font-medium rounded-xl text-sm"
                  style={{ backgroundColor: enconOrange }}
                  data-testid="ops-month-continue"
                >
                  {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Continue
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col h-[85vh]">
            {/* Header */}
            <div className="px-6 py-4 border-b bg-gray-50 pr-14">
              <div className="mb-2">
                <DialogTitle className="text-xl font-bold truncate" style={{ color: enconOrange }}>
                  Ops Review — {uploadedFile?.name}
                </DialogTitle>
                <div className="flex items-center gap-4 mt-1 flex-wrap">
                  {parseResult?.has_ext_transport ? (
                    <span className="flex items-center gap-1 text-sm text-green-600">
                      <CheckCircle className="w-4 h-4 flex-shrink-0" />
                      External Transport: {parseResult.ext_transport_orders} orders ({parseResult.ext_transport_matched} matched)
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-sm text-amber-600">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                      No External Transport tab found
                    </span>
                  )}
                  <span className="flex items-center gap-1 text-sm text-gray-500">
                    <Info className="w-4 h-4 flex-shrink-0" />
                    {errorEntries.length} entries · {selectedCount} selected
                  </span>
                  {parseResult?.has_error_log && (
                    <div className="flex items-center gap-1.5">
                      <label htmlFor="ops-review-month" className="text-xs text-gray-500 font-medium">Month:</label>
                      <select
                        id="ops-review-month"
                        value={selectedMonth}
                        onChange={(e) => fetchErrorsForMonth(e.target.value)}
                        disabled={fetchingMonth}
                        className="border border-gray-300 rounded-lg px-2 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-orange-400 disabled:opacity-50"
                        data-testid="ops-review-month-select"
                      >
                        {monthOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                      {fetchingMonth && <Loader2 className="w-3 h-3 animate-spin text-gray-400" />}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={selectAllErrors}
                  className="rounded-xl text-sm"
                  data-testid="select-all-errors"
                >
                  {Object.keys(selectedErrors).length === errorEntries.length && Object.values(selectedErrors).every(Boolean)
                    ? 'Deselect All' : 'Select All'}
                </Button>
                <Button
                  onClick={handleSaveDownload}
                  disabled={saving}
                  className="text-white font-medium rounded-xl text-sm"
                  style={{ backgroundColor: enconOrange }}
                  data-testid="save-download-ops"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Save & Download
                </Button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6">
              {errorEntries.length === 0 ? (
                <div className="text-center py-16 text-gray-400">
                  <Info className="w-12 h-12 mx-auto mb-4" />
                  <p className="text-lg font-medium">No error log entries found for this month</p>
                  <p className="text-sm mt-2">
                    {parseResult?.has_ext_transport
                      ? 'External Transport costs have been populated. Click Save & Download to get the updated file.'
                      : 'Click Save & Download to get the file as-is.'}
                  </p>
                </div>
              ) : (
                <>
                  {renderErrorSection('Picking Errors', pickingErrors, '#dc2626')}
                  {renderErrorSection('Other Errors', otherErrors, '#2563eb')}
                </>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OpsReviewModal;
